import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Button, Box, Typography, IconButton } from "@mui/material";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ErrorRoundedIcon from "@mui/icons-material/ErrorRounded";
import WarningRoundedIcon from "@mui/icons-material/WarningRounded";
import InfoRoundedIcon from "@mui/icons-material/InfoRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { C, LAYOUT } from "../constants";
import { friendlyNotice, friendlyTitle } from "../utils/userNotice";

const AppNotifyContext = createContext(null);

function getTone(name) {
  const tones = {
    info: {
      color: C.primary,
      bg: "#FFFFFF",
      soft: C.primarySoft,
      border: C.primary,
    },
    success: {
      color: C.success,
      bg: "#FFFFFF",
      soft: C.successBg,
      border: C.success,
    },
    warning: {
      color: C.warning,
      bg: "#FFFFFF",
      soft: C.warningBg,
      border: C.warning,
    },
    error: {
      color: C.error,
      bg: "#FFFFFF",
      soft: C.errorBg,
      border: C.error,
    },
    primary: {
      color: C.primaryDark,
      bg: "#FFFFFF",
      soft: C.primarySoft,
      border: C.primary,
    },
  };
  return tones[name] || tones.info;
}

const ICONS = {
  success: CheckCircleRoundedIcon,
  error: ErrorRoundedIcon,
  warning: WarningRoundedIcon,
  info: InfoRoundedIcon,
  primary: InfoRoundedIcon,
};

/**
 * Product-wide toast + confirm for AI Path Builder.
 */
export function AppNotifyProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const [dialog, setDialog] = useState(null);

  const dismiss = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const notify = useCallback((opts) => {
    const type = opts?.type || "info";
    const raw = String(opts?.message || opts?.msg || (typeof opts === "string" ? opts : "") || "").trim();
    const message = friendlyNotice(raw, type === "error" ? "Please try again in a moment." : "");
    if (!message) return;
    const id = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    const duration = opts?.duration ?? (type === "error" ? 8000 : 4500);
    setToasts((prev) => [...prev.slice(-3), {
      id,
      type,
      message,
      title: friendlyTitle(opts?.title, type),
      duration,
    }]);
    window.setTimeout(() => dismiss(id), duration);
  }, [dismiss]);

  const confirm = useCallback((opts = {}) => {
    return new Promise((resolve) => {
      setDialog({
        title: opts.title || "Please confirm",
        message: opts.message || "",
        confirmLabel: opts.confirmLabel || "Confirm",
        cancelLabel: opts.cancelLabel || "Cancel",
        tone: opts.tone || "primary",
        resolve,
      });
    });
  }, []);

  const closeDialog = (result) => {
    if (dialog?.resolve) dialog.resolve(result);
    setDialog(null);
  };

  useEffect(() => {
    const onEvent = (e) => {
      const d = e?.detail || {};
      notify({ type: d.type || "info", title: d.title, message: d.message || d.msg, duration: d.duration });
    };
    window.addEventListener("ap:notify", onEvent);
    return () => window.removeEventListener("ap:notify", onEvent);
  }, [notify]);

  const value = useMemo(() => ({ notify, confirm }), [notify, confirm]);
  const tone = getTone(dialog?.tone || "primary");

  return (
    <AppNotifyContext.Provider value={value}>
      {children}

      <Box
        sx={{
          position: "fixed",
          top: 18,
          right: 18,
          zIndex: 14000,
          display: "flex",
          flexDirection: "column",
          gap: 1.25,
          width: "calc(100% - 36px)",
          maxWidth: 380,
          pointerEvents: "none",
        }}
      >
        {toasts.map((toast) => {
          const t = getTone(toast.type);
          const Icon = ICONS[toast.type] || ICONS.info;
          return (
            <Box
              key={toast.id}
              role="status"
              sx={{
                pointerEvents: "auto",
                display: "flex",
                alignItems: "stretch",
                overflow: "hidden",
                borderRadius: "14px",
                background: t.bg,
                border: `1px solid ${t.border}33`,
                boxShadow: "0 10px 28px rgba(10,14,23,0.14), 0 2px 8px rgba(10,14,23,0.06)",
                animation: "apToastIn 240ms ease-out",
                "@keyframes apToastIn": {
                  from: { opacity: 0, transform: "translateY(-12px) scale(0.98)" },
                  to: { opacity: 1, transform: "translateY(0) scale(1)" },
                },
              }}
            >
              <Box sx={{ width: 6, flexShrink: 0, bgcolor: t.color }} />
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  gap: 1.25,
                  alignItems: "flex-start",
                  px: 1.5,
                  py: 1.35,
                  bgcolor: t.soft,
                }}
              >
                <Box
                  sx={{
                    mt: 0.15,
                    width: 32,
                    height: 32,
                    borderRadius: "50%",
                    bgcolor: "#fff",
                    color: t.color,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    boxShadow: "0 1px 3px rgba(10,14,23,0.08)",
                  }}
                >
                  <Icon sx={{ fontSize: 20 }} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0, pr: 0.5 }}>
                  <Typography
                    sx={{
                      fontWeight: 800,
                      fontSize: 13.5,
                      lineHeight: 1.3,
                      color: C.text,
                      letterSpacing: "-0.02em",
                    }}
                  >
                    {toast.title}
                  </Typography>
                  <Typography
                    sx={{
                      mt: 0.25,
                      fontWeight: 600,
                      fontSize: 13,
                      lineHeight: 1.45,
                      color: C.muted,
                    }}
                  >
                    {toast.message}
                  </Typography>
                </Box>
                <IconButton
                  size="small"
                  aria-label="Close"
                  onClick={() => dismiss(toast.id)}
                  sx={{
                    mt: -0.25,
                    mr: -0.5,
                    color: C.faint,
                    "&:hover": { color: C.text, bgcolor: "rgba(10,14,23,0.06)" },
                  }}
                >
                  <CloseRoundedIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Box>
            </Box>
          );
        })}
      </Box>

      {dialog && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            zIndex: 15001,
            background: "rgba(10,14,23,0.35)",
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-start",
            pt: `${LAYOUT.navbarHeight + 12}px`,
            px: 2,
          }}
          onClick={() => closeDialog(false)}
        >
          <Box
            onClick={(e) => e.stopPropagation()}
            sx={{
              width: "100%",
              maxWidth: 420,
              background: C.surface,
              borderRadius: `${LAYOUT.radius}px`,
              border: `1px solid ${C.border}`,
              boxShadow: "0 16px 40px rgba(10,14,23,0.2)",
              overflow: "hidden",
              fontFamily: "inherit",
            }}
          >
            <Box sx={{ height: 4, background: tone.color }} />
            <Box sx={{ px: 2.25, pt: 2, pb: 1 }}>
              <Typography
                sx={{
                  fontWeight: 800,
                  fontSize: 16,
                  letterSpacing: "-0.02em",
                  color: C.text,
                  mb: 0.75,
                }}
              >
                {dialog.title}
              </Typography>
              <Typography
                sx={{
                  fontSize: 13.5,
                  fontWeight: 600,
                  color: C.muted,
                  lineHeight: 1.5,
                }}
              >
                {dialog.message}
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 1,
                px: 2.25,
                pb: 2,
                pt: 1,
              }}
            >
              <Button
                onClick={() => closeDialog(false)}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  color: C.muted,
                  borderRadius: `${LAYOUT.radiusSm}px`,
                }}
              >
                {dialog.cancelLabel}
              </Button>
              <Button
                variant="contained"
                onClick={() => closeDialog(true)}
                sx={{
                  textTransform: "none",
                  fontWeight: 800,
                  borderRadius: `${LAYOUT.radiusSm}px`,
                  background: tone.color,
                  boxShadow: "none",
                  px: 2.25,
                  "&:hover": {
                    background: tone.color,
                    filter: "brightness(0.92)",
                    boxShadow: "none",
                  },
                }}
              >
                {dialog.confirmLabel}
              </Button>
            </Box>
          </Box>
        </Box>
      )}
    </AppNotifyContext.Provider>
  );
}

export function useAppNotify() {
  const ctx = useContext(AppNotifyContext);
  if (!ctx) {
    return {
      notify: ({ message, msg, type, title } = {}) => {
        try {
          window.dispatchEvent(new CustomEvent("ap:notify", { detail: { type, title, message: message || msg } }));
        } catch (_) {
          if (message || msg) console.info("[notify]", message || msg);
        }
      },
      confirm: async ({ message } = {}) =>
        typeof window !== "undefined" ? window.confirm(message || "Confirm?") : false,
    };
  }
  return ctx;
}

export default AppNotifyProvider;
